import React, { useEffect, useState } from 'react';

import { Divider, Grid, Link, Typography } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';
import { TranslateFunction } from 'react-localize-redux';

import { ListBox } from '@fd/ui/molecules';

import { KEYWORDS_MAX_LENGTH } from '../../constants';
import { KeywordChip } from '../KeywordChip/KeywordChip';

const useStyles = makeStyles((theme: Theme) => ({
  title: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
  },
  keywordCount: {
    textAlign: 'end',
    paddingRight: theme.spacing(4),
  },
  editList: {
    textAlign: 'end',
    paddingRight: theme.spacing(0.5),
  },
  errorTextColor: {
    color: '#ff395f',
  },
  errorMessagePadding: {
    paddingTop: theme.spacing(1),
  },
  clearList: {
    '&[disabled]': {
      color: 'grey',
      cursor: 'default',
      '&:hover': {
        textDecoration: 'none',
      },
    },
  },
}));

export type ListBoxProps = {
  title?: string;
  translate: TranslateFunction;
  keywords?: string[];
  setOpen?: (open: boolean) => void;
  isEdit?: boolean;
  clearKeywords?: () => void;
  handleDeleteKeyword?: (label: string) => void;
  height?: string;
};

export const KeywordsListBox = (props: ListBoxProps): JSX.Element => {
  const {
    clearKeywords,
    handleDeleteKeyword = undefined,
    height = undefined,
    isEdit = false,
    keywords = [],
    setOpen,
    title,
    translate,
  } = props;
  const [stateKeywords, setStateKeywords] = useState(keywords);
  const [keywordsLengthError, setKeywordsLengthError] = useState(keywords.length > 1);

  useEffect(() => {
    setStateKeywords(keywords);
  }, [keywords]);
  const renderListItems = () =>
    stateKeywords.map((keyWord) => (
      <KeywordChip handleDelete={handleDeleteKeyword} key={keyWord} label={keyWord} />
    ));

  const classes = useStyles();

  const headerButton = isEdit ? (
    <Link
      className={classes.clearList}
      disabled={stateKeywords.length === 0}
      component="button"
      type="button"
      data-fd={'clear-keywords-button'}
      onClick={() => clearKeywords?.()}
    >
      {translate('Clear_list')}
    </Link>
  ) : (
    <Link
      component="button"
      data-fd={'edit-list-button'}
      type="button"
      onClick={() => setOpen?.(true)}
    >
      {translate('Edit_list')}
    </Link>
  );
  const customHeader = (
    <>
      <Grid item xs={4} md={7}>
        <div className={classes.title}>
          <Typography variant="caption">{title}</Typography>
        </div>
        <Divider />
      </Grid>
      <Grid item xs={5} md={3}>
        <div className={clsx(classes.title, classes.editList)}>{headerButton}</div>
        <Divider />
      </Grid>
      <Grid item xs={3} md={2}>
        <div className={clsx(classes.title, classes.keywordCount)}>
          <Typography
            className={keywordsLengthError ? classes.errorTextColor : ''}
            variant="caption"
          >
            {stateKeywords.join(',').length}/{KEYWORDS_MAX_LENGTH}
          </Typography>
        </div>
        <Divider />
      </Grid>
    </>
  );

  useEffect(() => {
    stateKeywords.join(',').length > KEYWORDS_MAX_LENGTH
      ? setKeywordsLengthError(true)
      : setKeywordsLengthError(false);
  }, [stateKeywords]);
  return (
    <>
      <ListBox
        height={height}
        noItemsMessage={translate('No_keywords') as string}
        renderListItems={renderListItems}
        isEmpty={keywords.length === 0}
        customHeader={customHeader}
        direction={'row'}
        listPadding={1}
      />
      {keywordsLengthError && (
        <Grid item sm={12} className={classes.errorMessagePadding}>
          <Typography
            data-fd={'maximum-characters-message'}
            variant="caption"
            className={classes.errorTextColor}
          >
            {translate('A_maximum_of_100_characters_including_commas')}
          </Typography>
        </Grid>
      )}
    </>
  );
};
